import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Button,
  Card,
  CardContent,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import ListAltIcon from '@mui/icons-material/ListAlt';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../hooks/reduxHooks';
import transcriptionService from '../services/transcriptionService';
import type { SessionHistoryItem } from '../services/transcriptionService';
import { format } from 'date-fns';
import PageHeader from '../components/PageHeader';
import {
  fetchSystemHealth,
  selectOverallStatus,
  selectSystemHealth,
  selectHealthLoading,
} from '../store/systemHealthSlice';
import {
  statusIconColor,
  statusLabel,
  MODULE_LABELS,
} from '../services/systemHealthService';
import ReportStatusBadge from '../components/ReportStatusBadge';
import { typography } from '~/theme';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);
  const [sessions, setSessions] = useState<SessionHistoryItem[]>([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [loading, setLoading] = useState(true);

  // System health
  const overall = useAppSelector(selectOverallStatus);
  const health = useAppSelector(selectSystemHealth);
  const healthLoading = useAppSelector(selectHealthLoading);

  useEffect(() => {
    setLoading(true);
    transcriptionService
      .getSessionHistory({ page: 1, page_size: 10 })
      .then((data) => {
        setSessions(data.items);
        setTotalSessions(data.total);
      })
      .catch((err) => console.error('Failed to fetch sessions:', err))
      .finally(() => setLoading(false));

    dispatch(fetchSystemHealth());
  }, [dispatch]);

  const recentDictations = sessions.slice(0, 5);

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'MMM d, yyyy');
  };

  // Build tooltip content for system health
  const healthTooltip = (() => {
    if (!health) return 'Loading...';
    const modules = health.modules ?? {};
    const lines = Object.entries(modules).map(([key, mod]) => {
      const label = MODULE_LABELS[key] ?? key;
      return `${label}: ${statusLabel(mod.status)}`;
    });
    return lines.join('\n') || 'No modules';
  })();

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <PageHeader
        title="Dashboard"
        subtitle={`Welcome, ${user?.first_name ?? ''}`}
      />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: '280px 1fr',
          gridTemplateRows: '1fr auto',
          gap: 1.5,
        }}
      >
        {/* Quick Actions */}
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gridRow: '1 / -1' }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={typography.label.fontWeight}
            textTransform="uppercase"
            letterSpacing={0.5}
            sx={{ mb: 1.5 }}
          >
            Quick Actions
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<MicIcon />}
              onClick={() => navigate('/transcribe')}
              fullWidth
            >
              New Dictation
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ListAltIcon />}
              onClick={() => navigate('/transcriptions')}
              fullWidth
            >
              Dictation History
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={typography.label.fontWeight}
            textTransform="uppercase"
            letterSpacing={0.5}
            sx={{ mb: 1 }}
          >
            Activity
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h5" component="div">
                  {totalSessions}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Dictations
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="h5" component="div">
                  {sessions.filter(item => new Date(item.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This Week
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Compact System Status + Build Info */}
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <Tooltip
              title={<span style={{ whiteSpace: 'pre-line', fontSize: typography.caption.fontSize }}>{healthTooltip}</span>}
              arrow
              placement="top"
            >
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
                <FiberManualRecordIcon color={statusIconColor(overall)} sx={{ fontSize: 12 }} />
                <Typography variant="caption" fontWeight={typography.label.fontWeight} fontSize="0.7rem">
                  {statusLabel(overall)}
                </Typography>
                {healthLoading && <CircularProgress size={8} thickness={5} />}
              </Box>
            </Tooltip>
            {user?.role === 'admin' && (
              <Typography
                variant="caption"
                color="primary"
                sx={{ cursor: 'pointer', ml: 'auto', '&:hover': { textDecoration: 'underline' } }}
                onClick={() => navigate('/admin/monitor')}
              >
                Details
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Recent Dictations */}
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden', gridRow: '1 / -1' }}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={typography.label.fontWeight}
            textTransform="uppercase"
            letterSpacing={0.5}
            sx={{ mb: 1 }}
          >
            Recent Dictations
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress size={28} />
            </Box>
          ) : recentDictations.length === 0 ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                No dictations yet. Start by creating a new dictation.
              </Typography>
              <Button
                variant="contained"
                startIcon={<MicIcon />}
                onClick={() => navigate('/transcribe')}
              >
                New Dictation
              </Button>
            </Box>
          ) : (
            <List disablePadding sx={{ flex: 1, overflow: 'auto', mx: -2 }}>
              {recentDictations.map((item, index) => (
                <React.Fragment key={item.session_id}>
                  <ListItemButton
                    onClick={() => navigate(`/transcribe?session=${item.session_id}`)}
                    sx={{ px: 2, py: 1 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: typography.button.fontWeight }}>
                            {item.patient_name || 'Unknown Patient'}
                          </Typography>
                          <ReportStatusBadge status={item.report_lifecycle_status} />
                        </Box>
                      }
                      secondary={
                        <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span>{formatDate(item.created_at)}</span>
                          {item.cpt_code && (
                            <Chip
                              label={item.cpt_exam_name ? `${item.cpt_code} — ${item.cpt_exam_name.length > 30 ? item.cpt_exam_name.substring(0, 30) + '...' : item.cpt_exam_name}` : item.cpt_code}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: typography.overline.fontSize }}
                            />
                          )}
                        </Box>
                      }
                      secondaryTypographyProps={{ variant: 'caption', component: 'div' }}
                    />
                  </ListItemButton>
                  {index < recentDictations.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default Dashboard;